import { useId, useState, useMemo, useEffect } from 'react'
import {
  Columns3,
  Loader2,
  Search,
  Plus,
  Pencil,
  Trash2,
  Pin,
  PinOff,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { useColumnVisibilityStore } from '@/stores/useColumnVisibilityStore'
import {
  freezeScopeKey,
  useColumnFreezeStore,
  usePinnedColumnIds,
} from '@/stores/useColumnFreezeStore'
import {
  useColumnViews,
  useCreateView,
  useUpdateView,
  useDeleteView,
} from '@/hooks/useColumnViews'
import type { DagColumnMeta, UserColumnViewRead } from '@/types/dag'

const ALL_COLUMNS_VALUE = '__all__'

interface ColumnSelectorPopoverProps {
  columns: DagColumnMeta[]
  groups: string[]
  activeViewId?: string | null
  onSelectView?: (viewId: string | null) => void
  getCurrentColumnIds?: () => string[]
}

export function ColumnSelectorPopover({
  columns,
  groups,
  activeViewId = null,
  onSelectView,
  getCurrentColumnIds,
}: ColumnSelectorPopoverProps) {
  const idPrefix = useId()
  const [activeTab, setActiveTab] = useState<'columns' | 'views'>('columns')
  const [search, setSearch] = useState('')
  const [activeGroup, setActiveGroup] = useState<string | null>(null)
  const [showHiddenOnly, setShowHiddenOnly] = useState(false)

  const {
    hiddenColumns,
    toggleColumn,
    setColumnsHidden,
    removeStaleHiddenColumns,
    resetToDefaults,
    userHasCustomized,
  } = useColumnVisibilityStore()

  const freezeScope = freezeScopeKey(activeViewId)
  const pinnedColumnIds = usePinnedColumnIds(freezeScope)
  const togglePin = useColumnFreezeStore(s => s.togglePin)
  const removeStalePinnedColumns = useColumnFreezeStore(
    s => s.removeStalePinnedColumns
  )
  const clearFreezeScope = useColumnFreezeStore(s => s.clearScope)

  // ---- Views ----------------------------------------------------------
  const showViews = !!onSelectView && !!getCurrentColumnIds
  const { data: views } = useColumnViews()
  const createView = useCreateView()
  const updateView = useUpdateView()
  const deleteView = useDeleteView()

  const [viewDialogMode, setViewDialogMode] = useState<
    'create' | 'edit' | null
  >(null)
  const [editingView, setEditingView] = useState<UserColumnViewRead | null>(
    null
  )
  const [deleteTarget, setDeleteTarget] = useState<UserColumnViewRead | null>(
    null
  )
  const [viewName, setViewName] = useState('')
  const [viewDescription, setViewDescription] = useState('')

  const columnIdSet = useMemo(() => new Set(columns.map(c => c.id)), [columns])

  // Views are user-scoped, not plan-scoped — only offer views whose columns
  // all exist in this plan's DAG column catalog.
  const applicableViews = useMemo(
    () => (views ?? []).filter(v => v.columns.every(id => columnIdSet.has(id))),
    [views, columnIdSet]
  )
  const activeView = applicableViews.find(v => v.id === activeViewId) ?? null

  // Current frozen columns that are part of the view being saved, in view order.
  const frozenWithin = (columns: string[]) =>
    columns.filter(id => pinnedColumnIds.has(id))

  const handleOpenCreateDialog = () => {
    setViewDialogMode('create')
    setEditingView(null)
    setViewName('')
    setViewDescription('')
  }

  const handleOpenEditDialog = (view: UserColumnViewRead) => {
    setViewDialogMode('edit')
    setEditingView(view)
    setViewName(view.name)
    setViewDescription(view.description ?? '')
  }

  const handleCloseViewDialog = () => {
    setViewDialogMode(null)
    setEditingView(null)
  }

  const handleSubmitViewDialog = () => {
    const trimmedName = viewName.trim()
    if (!trimmedName) return

    if (viewDialogMode === 'edit' && editingView) {
      updateView.mutate(
        {
          viewId: editingView.id,
          payload: {
            name: trimmedName,
            description: viewDescription.trim() || null,
          },
        },
        { onSuccess: handleCloseViewDialog }
      )
      return
    }

    if (!getCurrentColumnIds) return
    const columns = getCurrentColumnIds()
    createView.mutate(
      {
        name: trimmedName,
        description: viewDescription.trim() || null,
        columns,
        frozen_columns: frozenWithin(columns),
      },
      {
        onSuccess: data => {
          handleCloseViewDialog()
          onSelectView?.(data.id)
          setActiveTab('columns')
        },
      }
    )
  }

  const handleUpdateCurrentViewColumns = () => {
    if (!activeView || !getCurrentColumnIds || updateView.isPending) return
    const columns = getCurrentColumnIds()
    updateView.mutate({
      viewId: activeView.id,
      payload: { columns, frozen_columns: frozenWithin(columns) },
    })
  }

  const handleDeleteConfirmed = () => {
    if (!deleteTarget) return
    deleteView.mutate(deleteTarget.id, {
      onSuccess: () => {
        if (activeViewId === deleteTarget.id) onSelectView?.(null)
        clearFreezeScope(freezeScopeKey(deleteTarget.id))
        setDeleteTarget(null)
      },
    })
  }

  // ---- Column visibility ------------------------------------------------
  const activeGroups = useMemo(() => {
    const groupsWithColumns = new Set(columns.map(c => c.group))
    return groups.filter(g => groupsWithColumns.has(g))
  }, [columns, groups])

  const columnIds = useMemo(() => new Set(columns.map(c => c.id)), [columns])
  const hiddenCount = useMemo(
    () => [...hiddenColumns].filter(id => columnIds.has(id)).length,
    [hiddenColumns, columnIds]
  )

  // Clean up stale hidden column IDs without marking as user-customized
  useEffect(() => {
    if (columns.length === 0) return
    const staleIds = [...hiddenColumns].filter(id => !columnIds.has(id))
    if (staleIds.length > 0) {
      removeStaleHiddenColumns(staleIds)
    }
  }, [columns]) // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up stale pinned column IDs
  useEffect(() => {
    if (columns.length === 0) return
    const staleIds = [...pinnedColumnIds].filter(id => !columnIds.has(id))
    if (staleIds.length > 0) {
      removeStalePinnedColumns(freezeScope, staleIds)
    }
  }, [columns]) // eslint-disable-line react-hooks/exhaustive-deps

  // Filter columns by search and group
  const filteredColumns = useMemo(() => {
    let result = columns
    if (showHiddenOnly) {
      result = result.filter(c => hiddenColumns.has(c.id))
    }
    if (activeGroup) {
      result = result.filter(c => c.group === activeGroup)
    }
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(c => c.label.toLowerCase().includes(q))
    }
    return result
  }, [columns, activeGroup, search, showHiddenOnly, hiddenColumns])

  // Group filtered columns by their group
  const groupedFiltered = useMemo(() => {
    const map = new Map<string, DagColumnMeta[]>()
    for (const col of filteredColumns) {
      const arr = map.get(col.group) ?? []
      arr.push(col)
      map.set(col.group, arr)
    }
    return map
  }, [filteredColumns])

  // Select all state for filtered columns
  const filteredIds = useMemo(
    () => filteredColumns.map(c => c.id),
    [filteredColumns]
  )
  const visibleFilteredCount = filteredIds.filter(
    id => !hiddenColumns.has(id)
  ).length
  const allChecked =
    filteredIds.length > 0 && visibleFilteredCount === filteredIds.length
  const someChecked =
    visibleFilteredCount > 0 && visibleFilteredCount < filteredIds.length

  const handleSelectAll = (checked: boolean) => {
    setColumnsHidden(filteredIds, !checked)
  }

  const columnsPanel = (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Search */}
      <div className="shrink-0 p-3 pb-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search columns..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
      </div>

      {/* Group filter pills */}
      <div className="shrink-0 flex gap-1.5 px-3 pb-2 overflow-x-auto">
        <Button
          variant="outline"
          size="sm"
          className={`cursor-pointer shrink-0 h-7 text-xs ${!activeGroup ? 'bg-primary text-white hover:bg-primary/90 hover:text-white' : ''}`}
          onClick={() => {
            setActiveGroup(null)
            setShowHiddenOnly(false)
          }}
        >
          All
        </Button>
        {activeGroups.map(group => (
          <Button
            key={group}
            variant="outline"
            size="sm"
            className={`cursor-pointer shrink-0 h-7 text-xs ${activeGroup === group ? 'bg-primary text-white hover:bg-primary/90 hover:text-white' : ''}`}
            onClick={() => {
              setActiveGroup(activeGroup === group ? null : group)
              setShowHiddenOnly(false)
            }}
          >
            {group}
          </Button>
        ))}
      </div>

      {/* Select all */}
      <div
        className="shrink-0 flex items-center gap-2 px-3 py-2 border-y border-border cursor-pointer select-none"
        onClick={() => handleSelectAll(!allChecked)}
      >
        <span className="pointer-events-none">
          <Checkbox
            checked={allChecked}
            {...(someChecked ? { 'data-state': 'indeterminate' } : {})}
          />
        </span>
        <span className="text-sm font-medium">Select all</span>
      </div>

      {/* Column list */}
      <div className="flex-1 min-h-0 overflow-y-auto py-1">
        {activeGroups
          .filter(g => groupedFiltered.has(g))
          .map(groupName => (
            <div key={groupName}>
              <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {groupName}
              </div>
              {groupedFiltered.get(groupName)!.map(col => {
                const isPinned = pinnedColumnIds.has(col.id)
                return (
                  <div
                    key={col.id}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-muted/50 cursor-pointer select-none"
                    onClick={() => toggleColumn(col.id)}
                  >
                    <span className="pointer-events-none">
                      <Checkbox checked={!hiddenColumns.has(col.id)} />
                    </span>
                    <span className="text-sm flex-1">{col.label}</span>
                    <button
                      type="button"
                      className={`cursor-pointer p-1 rounded hover:bg-muted ${
                        isPinned ? 'text-primary' : 'text-muted-foreground'
                      }`}
                      onClick={e => {
                        e.stopPropagation()
                        togglePin(freezeScope, col.id)
                      }}
                      title={isPinned ? 'Unfreeze column' : 'Freeze column'}
                    >
                      {isPinned ? (
                        <Pin className="h-3.5 w-3.5" />
                      ) : (
                        <PinOff className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                )
              })}
            </div>
          ))}
        {filteredColumns.length === 0 && (
          <div className="px-3 py-4 text-sm text-muted-foreground text-center">
            No columns match your search
          </div>
        )}
      </div>

      {/* Footer actions */}
      {(hiddenCount > 0 || userHasCustomized) && (
        <div className="shrink-0 border-t border-border px-3 py-2 flex flex-col gap-1">
          {hiddenCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className={`cursor-pointer w-full h-7 text-xs ${showHiddenOnly ? 'bg-primary text-white hover:bg-primary/90 hover:text-white' : ''}`}
              onClick={() => {
                setShowHiddenOnly(!showHiddenOnly)
                setActiveGroup(null)
              }}
            >
              {showHiddenOnly
                ? 'Show all columns'
                : `Show hidden columns (${hiddenCount})`}
            </Button>
          )}
          {userHasCustomized && (
            <Button
              variant="ghost"
              size="sm"
              className="cursor-pointer w-full h-7 text-xs text-muted-foreground"
              onClick={() => resetToDefaults()}
            >
              Reset to defaults
            </Button>
          )}
        </div>
      )}
    </div>
  )

  const viewsPanel = (
    <div className="flex-1 min-h-0 flex flex-col p-3 pt-2">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <RadioGroup
          value={activeViewId ?? ALL_COLUMNS_VALUE}
          onValueChange={value => {
            onSelectView?.(value === ALL_COLUMNS_VALUE ? null : value)
          }}
          className="gap-0.5"
        >
          <label
            htmlFor={`${idPrefix}-view-all`}
            className="flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-muted/50"
          >
            <RadioGroupItem
              value={ALL_COLUMNS_VALUE}
              id={`${idPrefix}-view-all`}
            />
            All columns
          </label>
          {applicableViews.map(view => (
            <div
              key={view.id}
              className="flex items-center gap-0.5 rounded hover:bg-muted/50"
            >
              <label
                htmlFor={`${idPrefix}-view-${view.id}`}
                className="flex items-start gap-2 flex-1 min-w-0 px-2 py-1.5 cursor-pointer"
              >
                <RadioGroupItem
                  value={view.id}
                  id={`${idPrefix}-view-${view.id}`}
                  className="mt-0.5"
                />
                <span className="min-w-0">
                  <span className="block text-sm truncate">{view.name}</span>
                  {view.description && (
                    <span className="block text-xs text-muted-foreground truncate">
                      {view.description}
                    </span>
                  )}
                </span>
              </label>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 cursor-pointer"
                onClick={() => handleOpenEditDialog(view)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 mr-1 cursor-pointer text-destructive"
                onClick={() => setDeleteTarget(view)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </RadioGroup>
        {applicableViews.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6 px-2">
            No saved views yet. Configure the columns you want in the Columns
            tab, then save them here as a view.
          </p>
        )}
      </div>
      <div className="shrink-0 flex items-center gap-2 pt-2 mt-2 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 h-8 text-xs cursor-pointer gap-1"
          onClick={handleOpenCreateDialog}
        >
          <Plus className="h-3.5 w-3.5" />
          New view
        </Button>
        {activeView && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs cursor-pointer gap-1"
            disabled={updateView.isPending}
            onClick={handleUpdateCurrentViewColumns}
          >
            {updateView.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            )}
            {updateView.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="gap-2 cursor-pointer">
            <Columns3 className="h-4 w-4" />
            {activeView ? activeView.name : 'Columns'}
            {hiddenCount > 0 && (
              <>
                <span className="h-4 w-px bg-border" />
                <span className="text-xs">{hiddenCount} hidden</span>
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-80 p-0 flex flex-col max-h-[min(30rem,var(--radix-popover-content-available-height))]"
        >
          {showViews ? (
            <Tabs
              value={activeTab}
              onValueChange={value =>
                setActiveTab(value as 'columns' | 'views')
              }
              className="flex-1 min-h-0 flex flex-col gap-0"
            >
              <div className="shrink-0 px-3 pt-3">
                <TabsList className="w-full">
                  <TabsTrigger
                    value="columns"
                    className="flex-1 cursor-pointer"
                  >
                    Columns
                  </TabsTrigger>
                  <TabsTrigger value="views" className="flex-1 cursor-pointer">
                    Views
                    {applicableViews.length > 0 &&
                      ` (${applicableViews.length})`}
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent
                value="columns"
                className="flex-1 min-h-0 flex flex-col mt-0"
              >
                {columnsPanel}
              </TabsContent>
              <TabsContent
                value="views"
                className="flex-1 min-h-0 flex flex-col mt-0"
              >
                {viewsPanel}
              </TabsContent>
            </Tabs>
          ) : (
            columnsPanel
          )}
        </PopoverContent>
      </Popover>

      {showViews && (
        <>
          {/* Create / edit view */}
          <Dialog
            open={viewDialogMode !== null}
            onOpenChange={open => !open && handleCloseViewDialog()}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {viewDialogMode === 'edit' ? 'Edit view' : 'Save as new view'}
                </DialogTitle>
                <DialogDescription>
                  {viewDialogMode === 'edit'
                    ? 'Update this view’s name and description.'
                    : 'Saves the columns currently shown, in their current order, as a reusable view.'}
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium" htmlFor="view-name">
                    Name
                  </label>
                  <Input
                    id="view-name"
                    value={viewName}
                    onChange={e => setViewName(e.target.value)}
                    placeholder="e.g. Forecasting"
                    autoFocus
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    className="text-sm font-medium"
                    htmlFor="view-description"
                  >
                    Description
                  </label>
                  <Textarea
                    id="view-description"
                    value={viewDescription}
                    onChange={e => setViewDescription(e.target.value)}
                    placeholder="What is this view for?"
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  className="cursor-pointer"
                  onClick={handleCloseViewDialog}
                >
                  Cancel
                </Button>
                <Button
                  className="cursor-pointer"
                  onClick={handleSubmitViewDialog}
                  disabled={
                    !viewName.trim() ||
                    createView.isPending ||
                    updateView.isPending
                  }
                >
                  {viewDialogMode === 'edit' ? 'Save' : 'Save view'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete confirmation */}
          <Dialog
            open={!!deleteTarget}
            onOpenChange={open => !open && setDeleteTarget(null)}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete view</DialogTitle>
                <DialogDescription>
                  {`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  className="cursor-pointer"
                  onClick={() => setDeleteTarget(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="cursor-pointer"
                  onClick={handleDeleteConfirmed}
                  disabled={deleteView.isPending}
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  )
}
